import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      nav("/app/overview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO pageKey="login" title="Log in — SerpNex" description="Log in to SerpNex — your daily rank-tracking dashboard." canonical="/login" />
      <div className="min-h-screen grid md:grid-cols-2 bg-white">
        <div className="hidden md:flex flex-col justify-between p-14 bg-[#0A0D14] text-white relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30" style={{ filter: 'invert(1)' }} />
          <Logo variant="dark" size="lg" />
          <div className="relative">
            <div className="eyebrow" style={{ color: '#7EA1FF' }}>Welcome back</div>
            <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold">Rankings first.<br />Reasons second.<br />Actions third.</h1>
            <p className="mt-6 text-white/60 max-w-md">SerpNex refreshes your rankings every 24 hours and explains every meaningful change.</p>
          </div>
          <div className="text-xs text-white/40 relative">Daily update cadence · Google-first · Transparent</div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-14">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
            <div className="md:hidden mb-8"><Logo /></div>
            <h2 className="text-3xl font-display font-bold text-[#0A0D14]">Log in.</h2>
            <p className="mt-2 text-sm text-[#525B6A]">New here? <Link to="/signup" className="text-[#0047FF] font-medium">Start a free trial</Link>.</p>

            <div className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Email</label>
                <input required type="email" data-testid="login-email" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-[#525B6A] mb-1 block">Password</label>
                <div className="relative">
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    data-testid="login-password"
                    className="w-full px-3 py-2.5 pr-10 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="login-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-[#8A94A6] hover:text-[#0047FF] focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" data-testid="login-forgot" className="text-xs text-[#525B6A] hover:text-[#0047FF]">Forgot password?</Link>
                </div>
              </div>
              <button disabled={busy} data-testid="login-submit" className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2">
                {busy ? "Logging in…" : (<>Log in <ArrowRight className="h-4 w-4" /></>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
